import React from "react";
import { Badge, Box, Button, Icon } from "@salestrak/ui";

import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { pluralize } from "#app/components/issue-view/activity/activity-utils";
import { formatSla, stageTypeLabel } from "#app/lib/entity-visuals";

import { DetailFieldRow } from "./detail-field-row";
import {
  EntityDetailShell,
  EntityDetailHeader,
  EntityDetailCard,
} from "./entity-detail-shell";

interface StageDetailDialogProps {
  stageId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function StageDetailDialog({ stageId, open, onOpenChange }: StageDetailDialogProps) {
  const { stageMap, workflowMap } = useWorkspaceData();
  const navigate = useAppNavigate();

  const stage = stageId ? stageMap.get(stageId) : undefined;
  const workflow = stage?.workflowId ? workflowMap.get(stage.workflowId) : undefined;

  if (!stage) return null;

  const typeLabel = stageTypeLabel(stage.type);
  const sla = formatSla(stage.age);
  const issueCount = stage.issueCount ?? 0;

  const openBoard = () => {
    onOpenChange(false);
    if (stage.workflowId) navigate.push(`/workflow/${stage.workflowId}`);
  };

  const header = (
    <EntityDetailHeader
      entity={{ kind: "stage", id: stage.stageId, stage }}
      badge={
        <Badge size="sm" variant="secondary">
          {typeLabel ?? "None"}
        </Badge>
      }
    />
  );

  const factsCard = (
    <EntityDetailCard>
      <Box display="grid" columns={1} gap="lg" className="sm:grid-cols-2">
        <DetailFieldRow icon="circle-dashed" label="Type" value={typeLabel} copyable />
        <DetailFieldRow
          icon="git-branch"
          label="Workflow"
          value={workflow?.name}
          truncate={1}
          copyable
        />
        <DetailFieldRow icon="timer" label="Age SLA" value={sla} />
        <DetailFieldRow
          icon="circle-dot"
          label="Projects"
          value={`${issueCount} ${pluralize("issue", { count: issueCount })}`}
        />
      </Box>
    </EntityDetailCard>
  );

  const footer = stage.workflowId ? (
    <Button className="w-full" onClick={openBoard}>
      <Icon name="layout-grid" />
      Open board
    </Button>
  ) : null;

  return (
    <EntityDetailShell
      open={open}
      onOpenChange={onOpenChange}
      header={header}
      srDescription={`${stage.name} stage details`}
      body={factsCard}
      footer={footer}
    />
  );
}
